import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./DoublesPaywall.module.scss"

/**
 * DoublesPaywall (`/doubles`) — a self-contained FEATURE-SPECIFIC paywall for the
 * real Premium **Duplicate Cleanup** feature of Private Photo Vault (it scans your
 * vault for near-identical bursts / re-saved copies and lets you keep the best
 * frame and reclaim the space the copies were eating).
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/doubles.md
 *
 * Duplicate detection has NEVER been the whole subject of a paywall here (it was a
 * throwaway line in Tidy's declutter pitch, nothing more). Every axis is chosen to
 * stay off the worn paths the ledger flags as saturated:
 *   - Feature:     Duplicate Cleanup — find the copies, keep one, get the space
 *                  back. (Never centered.)
 *   - Layout:      a DE-DUP SCAN CONSOLE — a data-viz storage meter that *recedes*
 *                  as space is reclaimed, sitting over a contact-sheet of burst
 *                  rows. No centered hero, no single glowing card.
 *   - Interaction: SCRUB THE SCANNER — drag a scan bar down the contact sheet; as
 *                  it passes each burst the near-duplicates COLLAPSE into their
 *                  keeper and the reclaimed-space counter climbs. Latches (you
 *                  can't un-clean by scrubbing back). Tap "Run cleanup" or keyboard
 *                  auto-drives it. First scrubbable-scanner mechanic.
 *   - Register:    satisfying / analytical relief — the oddly-satisfying feeling of
 *                  clutter collapsing and space coming back. Not fear, not luxury,
 *                  not the warm-reassuring renewal register the ledger says is now
 *                  saturated.
 *   - Art:         light DIAGNOSTIC INSTRUMENT — brand blue = kept / scanned, amber
 *                  (--color-secondary) = the wasted duplicate space. Real bundled
 *                  burst photos from `public/tidy/` (no network egress) read as a
 *                  camera roll instantly, but they live inside an instrument, not a
 *                  photographic hero.
 *   - Value:      DUPLICATE-CLEANUP + STORAGE-RECLAIM — both flagged untouched in
 *                  the ledger. The pitch is "you saved this moment five times; get
 *                  the space back," not privacy/security or backup-continuity.
 *
 * Honest & App Store compliant: Duplicate Cleanup is a real Premium feature. The
 * on-screen numbers are an explicitly-labelled sample scan of the demo photos on
 * the grid — not a claim about the viewer's own library. Nothing is deleted for
 * you (copy says you review and choose; originals stay untouched) and no data is
 * ever lost on expiration (never implied). No countdown, no fake scarcity; the
 * savings figure is exact yearly-vs-monthly arithmetic. Yearly is the featured,
 * default plan with the most prominent price; monthly is small and secondary.
 */

type Group = {
  key: string
  label: string
  keeper: string
  dupes: [string, string]
  mb: number
  tag: string // what kind of burst this is (honest, human)
}

// six real burst sets from the bundled `public/tidy/` demo library: one keeper +
// two near-identical copies each — exactly what Duplicate Cleanup collapses.
const GROUPS: Group[] = [
  {
    key: "beach",
    label: "Beach walk",
    keeper: "beach-1",
    dupes: ["beach-2", "beach-3"],
    mb: 96,
    tag: "3-shot burst",
  },
  {
    key: "pup",
    label: "The puppy",
    keeper: "pup-2",
    dupes: ["pup-1", "pup-3"],
    mb: 73,
    tag: "wouldn't sit still",
  },
  {
    key: "city",
    label: "City at dusk",
    keeper: "city-3",
    dupes: ["city-1", "city-2"],
    mb: 139,
    tag: "burst + a re-save",
  },
  {
    key: "food",
    label: "Dinner out",
    keeper: "food-2",
    dupes: ["food-1", "food-3"],
    mb: 62,
    tag: "one for the group chat",
  },
  {
    key: "blooms",
    label: "The blooms",
    keeper: "blooms-1",
    dupes: ["blooms-2", "blooms-3"],
    mb: 58,
    tag: "3-shot burst",
  },
  {
    key: "mountains",
    label: "Trailhead",
    keeper: "mountains-2",
    dupes: ["mountains-1", "mountains-3"],
    mb: 84,
    tag: "shared it twice",
  },
]

const TOTAL_MB = GROUPS.reduce((s, g) => s + g.mb, 0) // 512
const TOTAL_DUPES = GROUPS.length * 2 // 12
const USED_GB = 8.3 // sample vault size the meter represents

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

const src = (name: string) => `/tidy/${name}.jpg`

const fmtSpace = (mb: number) =>
  mb >= 1000 ? `${(mb / 1024).toFixed(2)} GB` : `${Math.round(mb)} MB`

type PlanKey = "yearly" | "monthly"

// ── little inline glyphs (no new asset deps) ─────────────────────────────────
const Shield = ({ size = 14 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M12 2.5l7 2.8v5.2c0 4.6-3 8.3-7 9.8-4-1.5-7-5.2-7-9.8V5.3l7-2.8z"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinejoin="round"
    />
    <path
      d="M8.6 12.2l2.3 2.3 4.4-4.6"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Tick = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M5 12.5l4.2 4.2L19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Copies = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <rect
      x={8.5}
      y={8.5}
      width={11}
      height={11}
      rx={2.2}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
    />
    <path
      d="M15.5 5.5H6a1.5 1.5 0 0 0-1.5 1.5V16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    />
  </svg>
)

const Scan = ({ size = 15 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
    <path
      d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
    />
    <path
      d="M4 12h16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
  </svg>
)

// eases a displayed number toward a target so counters tick rather than jump
function useCountUp(target: number, reduce: boolean | null) {
  const [val, setVal] = useState(0)
  const raf = useRef<number | null>(null)
  const cur = useRef(0)
  useEffect(() => {
    if (reduce) {
      cur.current = target
      setVal(target)
      return
    }
    const step = () => {
      const d = target - cur.current
      if (Math.abs(d) < 0.5) {
        cur.current = target
        setVal(target)
        raf.current = null
        return
      }
      cur.current += d * 0.18
      setVal(cur.current)
      raf.current = requestAnimationFrame(step)
    }
    if (raf.current == null) raf.current = requestAnimationFrame(step)
    return () => {
      if (raf.current != null) {
        cancelAnimationFrame(raf.current)
        raf.current = null
      }
    }
  }, [target, reduce])
  return val
}

const DoublesPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── scanner state ──────────────────────────────────────────────────────────
  // `progress` is the latched maximum the scan has reached (groups it passes stay
  // cleaned). `line` is the visible scan-bar position (follows the pointer / hint).
  const [progress, setProgress] = useState(0)
  const [line, setLine] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [hinted, setHinted] = useState(false)

  const sheetRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef(0)
  const raf = useRef<number | null>(null)
  const timers = useRef<number[]>([])

  useEffect(() => {
    progressRef.current = progress
  }, [progress])

  const clearRaf = useCallback(() => {
    if (raf.current != null) {
      cancelAnimationFrame(raf.current)
      raf.current = null
    }
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
  }, [])
  useEffect(() => () => clearRaf(), [clearRaf])

  // resolved groups: those whose row-centre has been passed by the latched scan
  const resolvedCount = useMemo(() => {
    let n = 0
    for (let i = 0; i < GROUPS.length; i++) {
      const centre = (i + 0.5) / GROUPS.length
      if (progress >= centre - 0.0001) n++
    }
    return n
  }, [progress])

  const reclaimedTarget = useMemo(
    () => GROUPS.slice(0, resolvedCount).reduce((s, g) => s + g.mb, 0),
    [resolvedCount],
  )
  const reclaimed = useCountUp(reclaimedTarget, reduceMotion)
  const dupesGone = resolvedCount * 2
  const done = resolvedCount >= GROUPS.length

  // teaching hint: the scan bar dips a little on load, once, then settles back
  useEffect(() => {
    if (reduceMotion) {
      setHinted(true)
      return
    }
    let cancelled = false
    const start = window.setTimeout(() => {
      const t0 = performance.now()
      const dur = 1150
      const tick = (now: number) => {
        if (cancelled) return
        const k = clamp((now - t0) / dur, 0, 1)
        const eased = k < 0.6 ? (k / 0.6) * 0.15 : 0.15 * (1 - (k - 0.6) / 0.4)
        setLine(eased)
        if (k < 1) raf.current = requestAnimationFrame(tick)
        else {
          setLine(0)
          setHinted(true)
        }
      }
      raf.current = requestAnimationFrame(tick)
    }, 650)
    return () => {
      cancelled = true
      window.clearTimeout(start)
      if (raf.current) cancelAnimationFrame(raf.current)
    }
  }, [reduceMotion])

  const yToFrac = (clientY: number) => {
    const el = sheetRef.current
    if (!el) return 0
    const r = el.getBoundingClientRect()
    return clamp((clientY - r.top) / r.height, 0, 1)
  }

  const applyLine = (frac: number) => {
    setLine(frac)
    setProgress((p) => Math.max(p, frac))
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if (done) return
    setHinted(true)
    clearRaf()
    setDragging(true)
    applyLine(yToFrac(e.clientY))
    sheetRef.current?.setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || done) return
    applyLine(yToFrac(e.clientY))
  }
  const endDrag = () => {
    if (!dragging) return
    setDragging(false)
    // settle the visible bar onto the latched progress line
    setLine(progressRef.current)
  }

  // tap / keyboard: sweep the scanner all the way down (accessible path)
  const runCleanup = () => {
    if (done) return
    setHinted(true)
    native.logMixpanelEvent("html_doubles_run", {})
    if (reduceMotion) {
      setProgress(1)
      setLine(1)
      return
    }
    clearRaf()
    const from = Math.max(progressRef.current, 0)
    const t0 = performance.now()
    const dur = 1700 * (1 - from)
    const tick = (now: number) => {
      const k = clamp((now - t0) / Math.max(dur, 1), 0, 1)
      const v = from + (1 - from) * k
      setLine(v)
      setProgress((p) => Math.max(p, v))
      if (k < 1) raf.current = requestAnimationFrame(tick)
      else {
        setLine(1)
        native.logMixpanelEvent("html_doubles_done", { freedMb: TOTAL_MB })
      }
    }
    raf.current = requestAnimationFrame(tick)
  }

  const reset = () => {
    clearRaf()
    setProgress(0)
    setLine(0)
    native.logMixpanelEvent("html_doubles_reset", {})
  }

  const choosePlan = (key: PlanKey) => {
    setPlan(key)
    native.logMixpanelEvent("html_pricing_toggle", { plan: key })
  }
  const handleClose = () => native.postNativeEvent("close")
  const purchase = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // ── derived display ────────────────────────────────────────────────────────
  // The meter shows the sample vault. `kept` (non-duplicate content) is a fixed
  // block; the amber `dup` block is the copies still on disk and shrinks to zero.
  const capacityMb = USED_GB * 1024
  const remainingDupMb = TOTAL_MB - reclaimed
  const dupPct = (remainingDupMb / capacityMb) * 100
  const keptPct = ((capacityMb - TOTAL_MB) / capacityMb) * 100
  const usedGbNow = (capacityMb - reclaimed) / 1024

  const status = done
    ? `Clean. ${TOTAL_DUPES} copies cleared — ${fmtSpace(TOTAL_MB)} back.`
    : dupesGone > 0
      ? `Merging copies… ${dupesGone} cleared, ${fmtSpace(
          Math.round(reclaimed),
        )} reclaimed`
      : hinted
        ? "Drag the scanner down — or tap Run cleanup"
        : "Scanner ready"

  const ctaLabel =
    plan === "yearly"
      ? hasTrial
        ? `Start ${trialDays} days free`
        : "Unlock Duplicate Cleanup"
      : "Choose monthly"
  const ctaSub =
    plan === "yearly"
      ? hasTrial
        ? `${trialDays} days free, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · auto-renews · cancel anytime`
      : `${monthlyPrice}/mo · auto-renews · cancel anytime`

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth={2.2}
            strokeLinecap="round"
          />
        </svg>
      </button>

      <header className={styles.head}>
        <div className={styles.brand}>
          <span className={styles.brandKey} aria-hidden>
            <Shield size={13} />
          </span>
          <span className={styles.brandName}>Private Photo Vault</span>
        </div>
        <span className={styles.featureChip}>
          <Copies size={12} /> Duplicate Cleanup
          <i className={styles.chipPremium}>Premium</i>
        </span>
        <h1 className={styles.title}>
          You saved this moment <em>five times.</em>
        </h1>
        <p className={styles.sub}>
          Bursts, screenshots, the same shot re-saved to send — your vault
          quietly fills with copies. <b>Duplicate Cleanup</b> finds them, keeps
          the best frame, and hands the space back.
        </p>
      </header>

      {/* ── the de-dup console ──────────────────────────────────────────────── */}
      <section
        className={styles.console}
        aria-label="Duplicate Cleanup preview"
      >
        {/* storage meter — recedes as space is reclaimed (data-viz) */}
        <div className={styles.meterRow}>
          <div className={styles.meterText}>
            <span className={styles.meterLabel}>Sample vault · space used</span>
            <span className={styles.meterUsed}>
              {usedGbNow.toFixed(2)}
              <span className={styles.meterUnit}> GB</span>
            </span>
          </div>
          <div className={styles.reclaimStat}>
            <span className={styles.reclaimNum}>
              {fmtSpace(Math.round(reclaimed))}
            </span>
            <span className={styles.reclaimCap}>reclaimed</span>
          </div>
        </div>
        <div className={styles.bar} aria-hidden>
          <span
            className={styles.barUsed}
            style={{ width: `${clamp(keptPct, 0, 100)}%` }}
          />
          <span
            className={styles.barDup}
            style={{ width: `${clamp(dupPct, 0, 100)}%` }}
          />
        </div>
        <div className={styles.legend} aria-hidden>
          <span className={styles.legKept}>
            <i /> kept
          </span>
          <span className={styles.legDup}>
            <i /> {done ? "copies cleared" : "duplicate copies"}
          </span>
          <span className={styles.legSample}>demo · sample library</span>
        </div>

        {/* contact sheet + draggable scanner */}
        <div
          ref={sheetRef}
          className={`${styles.sheet} ${dragging ? styles.sheetDrag : ""}`}
          role="button"
          tabIndex={0}
          aria-label="Drag the scanner down the photos to merge duplicates"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              runCleanup()
            }
          }}
        >
          {GROUPS.map((g, i) => {
            const centre = (i + 0.5) / GROUPS.length
            const resolved = progress >= centre - 0.0001
            return (
              <div
                key={g.key}
                className={`${styles.grp} ${resolved ? styles.grpDone : ""}`}
              >
                <div className={styles.stack}>
                  {/* two duplicate copies fanned behind, collapse into keeper */}
                  <span className={`${styles.copy} ${styles.copyB}`}>
                    <img src={src(g.dupes[1])} alt="" draggable={false} />
                  </span>
                  <span className={`${styles.copy} ${styles.copyA}`}>
                    <img src={src(g.dupes[0])} alt="" draggable={false} />
                  </span>
                  <span className={styles.keeper}>
                    <img src={src(g.keeper)} alt="" draggable={false} />
                    <span className={styles.keepFlag} aria-hidden>
                      <Tick size={10} />
                    </span>
                  </span>
                  {!resolved && <span className={styles.countChip}>×3</span>}
                </div>

                <div className={styles.meta}>
                  <span className={styles.grpLabel}>{g.label}</span>
                  <span className={styles.grpTag}>{g.tag}</span>
                </div>

                <div className={styles.grpRight}>
                  {resolved ? (
                    <span className={styles.freed}>
                      <b>+{fmtSpace(g.mb)}</b>
                      <span>kept 1 · freed 2</span>
                    </span>
                  ) : (
                    <span className={styles.waste}>
                      <b>{fmtSpace(g.mb)}</b>
                      <span>2 copies</span>
                    </span>
                  )}
                </div>
              </div>
            )
          })}

          {/* the scan bar the user drags */}
          <div
            className={`${styles.scanBar} ${done ? styles.scanDone : ""}`}
            style={{ top: `${(dragging || !hinted ? line : progress) * 100}%` }}
            aria-hidden
          >
            <span className={styles.scanGlow} />
            <span className={styles.scanKnob}>
              <Scan size={13} />
            </span>
          </div>
        </div>

        <div className={styles.statusRow}>
          <span
            className={`${styles.statusDot} ${done ? styles.statusDotOn : ""}`}
            aria-hidden
          />
          <span className={styles.status} aria-live="polite">
            {status}
          </span>
        </div>

        <div className={styles.consoleAction}>
          {!done ? (
            <button className={styles.runBtn} onClick={runCleanup}>
              <Scan size={15} /> Run cleanup
            </button>
          ) : (
            <button className={styles.againBtn} onClick={reset}>
              Scan again
            </button>
          )}
        </div>
      </section>

      {/* what Duplicate Cleanup actually does — honest, short */}
      <ul className={styles.does}>
        {[
          "Groups bursts & re-saved copies, and picks the sharpest keeper",
          "You review every set — nothing is removed without your tap",
          "Reclaims the space the copies were using; originals untouched",
        ].map((t) => (
          <li key={t}>
            <span className={styles.doesDot} aria-hidden>
              <Tick size={10} />
            </span>
            {t}
          </li>
        ))}
      </ul>

      {/* ── plans — yearly featured, monthly small & secondary ──────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${
            plan === "yearly" ? styles.planSel : ""
          }`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.planTop}>
            <span className={styles.planName}>Yearly</span>
            {savingsPct > 0 && (
              <span className={styles.saveBadge}>SAVE {savingsPct}%</span>
            )}
          </span>
          <span className={styles.planNote}>
            Copies pile up every week — keep Duplicate Cleanup running all year
          </span>
          <span className={styles.planPriceRow}>
            <span className={styles.planBig}>{yearlyPrice}</span>
            <span className={styles.planPer}>/year</span>
            <span className={styles.planSmall}>≈ {yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${
            plan === "monthly" ? styles.planMonthSel : ""
          }`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planMonthName}>Monthly</span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={purchase}>
        <span className={styles.ctaLabel}>{ctaLabel}</span>
        <span className={styles.ctaSub}>{ctaSub}</span>
      </button>

      <p className={styles.reassure}>
        You're always in control — Duplicate Cleanup only suggests copies, and
        you choose what to remove. Your keepers and originals stay right where
        they are.
      </p>

      <div className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>
    </div>
  )
}

export default DoublesPaywall
